import React, { useEffect } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';

import RichTextEditor from '../SlateEditor/RichTextEditor';
import htmlSerializer from '../SlateEditor/htmlSerializer';

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(3),
    },
    menuLeft: {
        maxHeight: 750,
        overflowY: 'scroll',
        borderRight: '1px solid #f1f1f1'
    },
    menuLeftItem: {
        cursor: 'pointer',
        padding: theme.spacing(1),
        borderBottom: 'solid 1px #ddd',
        '&:hover': {
            backgroundColor: '#ddd'
        },
        '& .selected': {
            color: theme.palette.primary.main
        }
    },
    fieldRow: {
        cursor: 'pointer',
        width: '100%',
        minHeight: 20,
        border: '1px solid #515656',
        borderRadius: 5,
        padding: 10,
        '&:hover': {
            backgroundColor: '#ddd'
        },
    },
    fieldEditor: {
        border: `1px solid #515656`,
        borderRadius: 5,
        backgroundColor: '#f9f9f9',
        marginBottom: theme.spacing(2),
        padding: 10,
        '& .MuiTextField-root': {
            marginBottom: '-10px',
            marginTop: 0
        },
        '& .MuiInput-underline:before': {
            border: 'none'
        },
        '& .MuiInput-underline:hover:before': {
            border: 'none'
        }
    },
    emailName: {
        color: theme.palette.primary.main,
        marginBottom: 10
    },
    lastUpdated: {
        color: theme.palette.text.secondary
    }
}));

const shortenText = (text, length) => {
    if (text.length > length) {
        return text.substring(0, length) + "...";
    }
    return text;
}

const FieldEditor = props => {
    const editorRef = React.createRef();

    const {
        id,
        type,
        html,
        handleFieldClick,
        handleSave,
    } = props;

    const classes = useStyles();

    const [value, setValue] = React.useState(html)

    const handleChange = event => {
        setValue(event.target.value);
    }

    // There were some issues with using the RichTextEditor with email bodies, so for now, we won't use it
    const RICH_TEXT_FIELD = 'NONE';

    const handleOnSave = () => {
        let fieldValue;
        if (type === RICH_TEXT_FIELD) {
            fieldValue = htmlSerializer.serialize(editorRef.current.value);
        } else {
            fieldValue = value;
        }
        const data = {
            _token: window._token,
            [type]: fieldValue
        };
        const url = `/api/admin/email-templates/${id}`;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                return response.json();
            })
            .then(result => {
                handleSave(result.template);
                handleFieldClick(type);
            })
            .catch(reason => {
                alert('An error prevented saving changes to the email template. Please refresh the page and try again.');
            });
    }

    return (
        <div className={classes.fieldEditor}>
            {type === RICH_TEXT_FIELD && (
                <RichTextEditor
                    initialValue={html}
                    ref={editorRef}
                />
            )}
            {type !== RICH_TEXT_FIELD && (
                <TextField
                    fullWidth
                    margin="normal"
                    value={value || ''}
                    onChange={handleChange}
                    multiline={type === 'message'}
                />
            )}
            <hr />
            <div className={classes.fieldEditorActions}>
                <Button onClick={() => handleFieldClick(type)} variant="outlined">Cancel</Button>
                <Button onClick={handleOnSave} className="ml-1" variant="outlined" color="primary">Save</Button>
            </div>
        </div>
    )
}

const FieldRow = props => {
    const {
        id,
        editable,
        editing,
        label,
        type,
        value,
        handleFieldClick,
        handleSave
    } = props;

    const classes = useStyles();

    if (editable) {
        return (
            <React.Fragment>
                {editing[type] && (
                    <div>
                        <p className="mb-0"><strong>{label}:</strong></p>
                        <FieldEditor id={id} type={type} html={value} handleFieldClick={handleFieldClick} handleSave={handleSave} />
                    </div>
                )}
                {!editing[type] && (
                    <React.Fragment>
                        <p className="mb-0"><strong>{label}:</strong></p>
                        <p title="Click to edit" className={classes.fieldRow} onClick={() => handleFieldClick(type)}>{value || '(not set)'}</p>
                    </React.Fragment>
                )}
            </React.Fragment>
        )
    }
    return (
        <p><strong>{label}</strong>:<br />{value}</p>
    )
}

const SearchBar = ({ templates, setTemplates }) => {
    const [searchText, setSearchText] = React.useState('');

    useEffect(() => {
        filterTemplates();
    }, [templates, searchText]);

    const filterTemplates = () => {
        if (!searchText) {
            setTemplates(templates);
            return;
        }
        const filterText = searchText.toLowerCase();
        const newTemplates = templates.filter(item => {
            const itemText = [item.name, item.subject, item.message].join(', ').toLowerCase();
            return itemText.indexOf(filterText) >= 0;
        });
        setTemplates(newTemplates);
    }

    const handleChange = event => {
        const value = event.target.value;
        setSearchText(value);
    }

    return (
        <div>
            <TextField
                id='email-templates-search'
                variant='outlined'
                margin='dense'
                placeholder='Search'
                fullWidth
                value={searchText}
                onChange={handleChange}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
            />
        </div>
    )
}

const TemplateSelector = props => {
    const {
        fetchData,
        templates,
        setOpenSnackbar,
        setSnackbarMessage
    } = props;

    const classes = useStyles();

    const [selected, setSelected] = React.useState(null);

    const [filteredTemplates, setFilteredTemplates] = React.useState([]);

    const defaultEditing = {
        message: false,
        subject: false
    };
    const [editing, setEditing] = React.useState(defaultEditing);

    const handleClick = item => {
        if (selected && selected.id === item.id) {
            setSelected(null);
        } else {
            setSelected(item);
            setEditing(defaultEditing);
        }
    }

    const handleSave = template => {
        setSnackbarMessage('Successfully saved changes to the email template');
        setOpenSnackbar(true);
        setSelected(template);
        fetchData();
    }

    const handleFieldClick = type => {
        if (type === 'message') {
            setEditing({
                message: !editing.message,
                subject: false
            });
        } else if (type === 'subject') {
            setEditing({
                message: false,
                subject: !editing.subject
            });
        }
    }

    if (templates) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12} sm={3} className={classes.menuLeft}>
                    <SearchBar templates={templates} setTemplates={setFilteredTemplates} />
                    {filteredTemplates.map((item, key) => (
                        <div key={key} onClick={() => handleClick(item)} className={classes.menuLeftItem}>
                            <p className={selected && selected.id === item.id ? 'mb-0 selected' : 'mb-0'}><strong>{item.name}</strong></p>
                            <p className="mb-0">{shortenText(item.message, 120)}</p>
                        </div>
                    ))}
                </Grid>
                <Grid item xs={12} sm={9}>
                    {selected && (
                        <div>
                            <p className={classes.emailName}><strong>{selected.name}</strong></p>
                            <FieldRow
                                id={selected.id}
                                editable={true}
                                editing={editing}
                                label='Subject'
                                type='subject'
                                value={selected.subject}
                                handleFieldClick={handleFieldClick}
                                handleSave={handleSave}
                            />
                            <FieldRow
                                id={selected.id}
                                editable={true}
                                editing={editing}
                                label='Message'
                                type='message'
                                value={selected.message}
                                handleFieldClick={handleFieldClick}
                                handleSave={handleSave}
                            />
                            <hr />
                            <p className={classes.lastUpdated}>Last updated {selected.updated_at_formatted}</p>
                        </div>
                    )}
                    {!selected && (
                        <p>Please select an email</p>
                    )}
                </Grid>
            </Grid>
        )
    }
}

const EmailTemplates = () => {
    const classes = useStyles();

    const [templates, setTemplates] = React.useState([]);

    const fetchData = () => {
        const url = '/api/admin/email-templates';
        fetch(url)
            .then(response => {
                if (response.status != 200) {
                    throw Error("Invalid response. Status: "  + response.status);
                }
                return response.json();
            })
            .then(result => {
                setTemplates(result);
            })
            .catch(error => {
                console.log(error);
            })
    }

    useEffect(() => {
        document.title = 'Email Templates - Enduring Promises';
        fetchData();
    }, []);

    const [openSnackbar, setOpenSnackbar] = React.useState(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    return (
        <Paper square className={classes.paper}>
            <Typography variant="h6" className="mb-3">Email Templates</Typography>
            <TemplateSelector fetchData={fetchData} templates={templates} setOpenSnackbar={setOpenSnackbar} setSnackbarMessage={setSnackbarMessage} />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbarMessage}
                action={
                    <React.Fragment>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </React.Fragment>
                }
            />
        </Paper>
    )
}

export default EmailTemplates;
